import type { PcMonitorListing } from '../../types/pcmonitor.ts';
import { formatChf } from './listingMath.ts';
import ListingImage from './ListingImage.tsx';
import AskingPrice from './AskingPrice.tsx';
import ValueGap from './ValueGap.tsx';
import ListingAge from './ListingAge.tsx';
import PriceDrop from './PriceDrop.tsx';
import VerdictBadge from './VerdictBadge.tsx';
import ListingFlags from './ListingFlags.tsx';

export function AdvertLink({ listing, className = '' }: { listing: PcMonitorListing; className?: string }) {
  return (
    <a
      href={listing.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`font-medium text-gray-900 hover:text-blue-700 hover:underline ${className}`}
    >
      {listing.title}
    </a>
  );
}

export function ProviderLine({ listing }: { listing: PcMonitorListing }) {
  return (
    <span className="text-xs text-gray-500">
      {listing.provider}
      {listing.location ? ` · ${listing.location}` : ''}
      {listing.posted_date ? ` · posted ${listing.posted_date}` : ''}
    </span>
  );
}

interface ListingRowProps {
  listing: PcMonitorListing;
}

/** Desktop table row (md and up). The mobile card lives in ListingCard. */
export default function ListingRow({ listing }: ListingRowProps) {
  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50 align-top">
      <td className="py-3 pr-3 pl-3">
        <a href={listing.url} target="_blank" rel="noopener noreferrer" aria-label={listing.title}>
          <ListingImage thumbnailUrl={listing.thumbnail_url} title={listing.title} />
        </a>
      </td>
      <td className="py-3 pr-3 max-w-md">
        <div className="space-y-1">
          <AdvertLink listing={listing} className="text-sm leading-snug block" />
          <ProviderLine listing={listing} />
          <ListingFlags
            highPriority={listing.high_priority}
            cardOnly={listing.card_only}
            flags={listing.flags}
          />
        </div>
      </td>
      <td className="py-3 pr-3 whitespace-nowrap text-sm text-gray-700">
        <ListingAge firstSeen={listing.first_seen} />
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <AskingPrice listing={listing} />
      </td>
      <td className="py-3 pr-3 whitespace-nowrap tabular-nums text-sm text-gray-700">
        {listing.estimated_value != null ? formatChf(listing.estimated_value) : '—'}
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <ValueGap listing={listing} />
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <PriceDrop history={listing.price_history} />
      </td>
      <td className="py-3 pr-3">
        <VerdictBadge verdict={listing.verdict} rejectionReasons={listing.rejection_reasons} />
      </td>
    </tr>
  );
}
